/**
 * Shared date-range state for every report.
 *
 * Defaults to the financial year to date, which is what an accountant
 * almost always wants, and remembers the choice for the session so moving
 * between reports does not reset it.
 */
import { useCallback, useState } from 'react'
import { today } from '../dates'

const KEY = 'bv.range'

export interface Range {
  from: string; to: string
  /** The quick period last chosen. On the 1st of a month "today" and "this
   *  month" are the same dates, so the dates alone cannot say which is on. */
  preset?: PresetName
}


const yearStart = () => `${new Date().getFullYear()}-01-01`

function load(): Range {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (raw) return JSON.parse(raw) as Range
  } catch { /* ignore */ }
  return { from: yearStart(), to: today() }
}

export function useDateRange() {
  const [range, setRangeState] = useState<Range>(load)

  const setRange = useCallback((next: Range) => {
    setRangeState(next)
    try { sessionStorage.setItem(KEY, JSON.stringify(next)) } catch { /* ignore */ }
  }, [])

  const presets = {
    today: () => setRange({ from: today(), to: today(), preset: 'today' }),
    month: () => setRange({ from: monthStart(), to: today(), preset: 'month' }),
    quarter: () => setRange({ from: quarterStart(), to: today(), preset: 'quarter' }),
    year: () => setRange({ from: yearStart(), to: today(), preset: 'year' }),
  }

  return { range, setRange, presets, active: activePreset(range) }
}

/** The calendar quarter we are in, from its first day. */
function quarterStart(): string {
  const d = new Date()
  const m = Math.floor(d.getMonth() / 3) * 3 + 1
  return `${d.getFullYear()}-${String(m).padStart(2, '0')}-01`
}

function monthStart(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

export type PresetName = 'today' | 'month' | 'quarter' | 'year'

/** Which quick period the range is, if any -- so its button shows as on. */
export function activePreset(r: Range): PresetName | null {
  if (r.to !== today()) return null
  const starts: Record<PresetName, () => string> = {
    today, month: monthStart, quarter: quarterStart, year: yearStart }
  if (r.preset && r.from === starts[r.preset]()) return r.preset
  if (r.from === today()) return 'today'
  if (r.from === monthStart()) return 'month'
  if (r.from === quarterStart()) return 'quarter'
  if (r.from === yearStart()) return 'year'
  return null
}
